/* eslint-disable no-plusplus */
import { mutateFn } from '@/utils/use-complex-state';
import {
  ElementTracking,
  Experience,
  toTargetingDeviceType,
} from '@/webapi/use-experience-api';
import { resolveChangeTargetPlacement } from '@/features/editor/context/use-experience-state';

const MAX_TRACKING = 12;

export const makeDuplicateElementTracking =
  (
    setCurrentExperience: (
      fn: mutateFn<Experience>,
      ignoreHistory?: boolean,
      forceSameVersion?: boolean,
    ) => void,
  ) =>
  (elementTracking: ElementTracking) => {
    setCurrentExperience((draft) => {
      const next = makeLowestAvailableOrdinal(draft?.elementTracking);
      if (!next) return;
      const source = draft?.elementTracking?.find(
        (e) => e.ordinal === elementTracking.ordinal,
      );
      const clone = {
        ...source,
        ...elementTracking,
        name: `${source.name} (dup)`,
        ordinal: next,
      };
      draft.elementTracking = [...draft.elementTracking, clone];
    });
  };

export const makeSetElementTracking =
  (setCurrentExperience: (fn: mutateFn<Experience>) => void) =>
  (elementTracking: ElementTracking) => {
    setCurrentExperience((draft) => {
      const existing = draft?.elementTracking?.findIndex(
        (e) =>
          e.ordinal === elementTracking.ordinal && elementTracking.ordinal >= 1,
      );

      if (existing >= 0) {
        draft.elementTracking = [
          ...draft.elementTracking.map((et, i) =>
            i === existing ? elementTracking : et,
          ),
        ];
        return;
      }

      const next = makeLowestAvailableOrdinal(draft?.elementTracking);
      if (!next) return;
      const targetPlacement = getTargetPlacement(draft);
      const targetDevice = toTargetingDeviceType(draft.quickPreviewDevice);
      draft.elementTracking = [
        ...(draft?.elementTracking || []),
        {
          ...elementTracking,
          ordinal: next,
          targetPlacement,
          targetDevice,
        },
      ];
    });
  };

const getUsedOrdinals = (list?: ElementTracking[]) => {
  const set = new Set<number>();
  (list || []).forEach((e) => {
    set.add(e.ordinal);
  });
  return set;
};

const makeLowestAvailableOrdinal = (list?: ElementTracking[]) => {
  const used = getUsedOrdinals(list);
  for (let i = 1; i <= MAX_TRACKING; i++) {
    if (!used.has(i)) return i;
  }
  return null;
};

export const getDeleteElementTracking =
  (setCurrentExperience: (fn: mutateFn<Experience>) => void) =>
  (elementTracking: ElementTracking) => {
    setCurrentExperience((draft) => {
      const existing = draft?.elementTracking?.findIndex(
        (e) => e.ordinal === elementTracking.ordinal,
      );
      if (existing >= 0) {
        draft.elementTracking = draft.elementTracking?.filter(
          (x) => x.ordinal !== elementTracking.ordinal,
        );
      }
    });
  };

export const getTargetPlacement = (draft: Experience) =>
  resolveChangeTargetPlacement(
    draft?.targeting?.placement?.kind,
    {
      kind: draft?.quickPreviewPlacement,
      other: draft?.targeting?.other,
    },
    draft?.quickPreviewUrl,
  );
